// src/components/ClientDetails/AdicionarLead.tsx
import { useForm } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { X, Upload, AlertCircle } from "lucide-react";
import { leadSchema, leadSchemaParceiroCrm, LeadForm, LeadFormParceiroCrm } from './types';
import { fetchWithAuth } from '@/lib/fetchWithAuth';
import { usePermissions } from '@/hooks/usePermissions';
import { toast } from 'sonner';
import { useVendedoresSimples } from '@/hooks/useVendedoresSimples';
import { useParceiros } from '@/hooks/useParceiros';
import {
  LEAD_PRODUTO_VALORES,
  PRODUTO_LEAD_PARCERIA_CRM,
  isProdutoLeadSolarComoOnGrid,
  PRODUTO_LEAD_SEM_PRODUTO,
  type LeadProdutoValor,
} from '@/constants/leadsProduto';
import { CRM_FUNIL_PARCERIA_PROSPECCAO, CRM_FUNIL_VENDAS, type CrmFunilTipo } from '@/constants/crmFunil';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import ClienteExistenteCombobox, {
  type ClienteCadastroOption,
} from './ClienteExistenteCombobox';
import { formatNomeProprio } from '@/utils/formatNomeProprio';

// Opções de origem
const origemOptions = [
  "Leads de Representantes",
  "Cold Email Marketing",
  "Cold Calls (Call Center)",
  "Pesquisas",
  "Google Ads",
  "Facebook Ads",
  "Campanhas de Tráfego Pago",
  "Campanhas de Indicação",
  "Captação Site (SEO)",
  "Canal YouTube",
  "Plataforma IA-BestSolar",
  "Calculadoras",
  "E-Books",
  "LinkedIn",
  "Instagram",
  "Google Meu Negócio",
  "Parceiros",
  "Outro",
];

// Opções para Tipo de Fixação e Telha
const tipoFixacaoOptions = ["Telhado", "Solo", "Carport"];
const tipoTelhaOptions = ["Telha Metálica", "Telha Cerâmica", "Telha de Fibrocimento", "Laje", "Telha zipada"];

/** Catálogo completo de `leads.produto` (página Clientes, pipeline vendas, etc.). */
const produtoOptions = [...LEAD_PRODUTO_VALORES];

const DEFAULT_EXCLUDED: string[] = [];

// Funções de formatação
const formatCNPJ = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 14);
  return value.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

const formatCPF = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  return value.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

const formatCEP = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 8);
  return value.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

const formatTelefone = (value: string) => {
  value = value.replace(/\D/g, '').slice(0, 11);
  if (value.length === 11) {
    return value.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  } else if (value.length === 10) {
    return value.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
  }
  return value;
};

const aplicarFormatoNomeNoBlur = (
  valor: string,
  onChange: (value: string) => void
) => {
  const formatado = formatNomeProprio(valor);
  if (formatado !== valor) {
    onChange(formatado);
  }
};

interface AdicionarLeadProps {
  onClose: () => void;
  onSuccess?: () => void;
  defaultProduct?: LeadProdutoValor;
  lockProduct?: boolean; // Se true, o campo produto será desabilitado e fixo no defaultProduct
  excludedProducts?: string[]; // Lista de produtos que devem ser excluídos das opções
  /**
   * Restringe o select de produto a estes valores (ordem = ordem no catálogo).
   * Com `lockProduct` e 2+ itens aqui, o select permanece habilitado para escolher dentro do conjunto
   * (ex.: página Adequações: FV vs Elétricas).
   */
  allowedProductOptions?: readonly string[];
  /**
   * Quando true, permite marcar vários produtos; o backend cria um lead (negociação) por produto.
   * Ideal nas páginas Carregadores e Adequações.
   */
  allowMultipleProducts?: boolean;
  /**
   * Quando true, produtos já vinculados ao cliente continuam selecionáveis
   * (cria outra negociação/proposta do mesmo produto — ex.: Adequações e Reformas).
   */
  allowDuplicateProducts?: boolean;
  /** Quando `parceiro_prospeccao`, cadastro vai apenas para o funil CRM Parceiros (não aparece em Clientes). */
  crmFunil?: CrmFunilTipo;
}

export default function AdicionarLead({
  onClose,
  onSuccess,
  defaultProduct,
  lockProduct = false,
  excludedProducts = DEFAULT_EXCLUDED,
  allowedProductOptions,
  allowMultipleProducts = false,
  allowDuplicateProducts = false,
  crmFunil = CRM_FUNIL_VENDAS,
}: AdicionarLeadProps) {
  const permissions = usePermissions();
  const { vendedores, loading: vendedoresLoading } = useVendedoresSimples();
  // Role helpers
  const isAdmin = permissions?.isAdmin?.() || false;
  const isRepresentative = permissions?.isRepresentative?.() || false;
  const isPartner = permissions?.isPartner?.() || false;
  const isSeller = permissions?.hasRole ? permissions.hasRole('vendedor' as any) : false;
  const isParceiroCrm = crmFunil === CRM_FUNIL_PARCERIA_PROSPECCAO;

  /** Funil vendas / Clientes: não oferecer prospecção de parceria (exclusivo do CRM Parceiros). */
  const catalogBase = isParceiroCrm
    ? produtoOptions
    : produtoOptions.filter((option) => option !== PRODUTO_LEAD_PARCERIA_CRM);

  // Determinar o produto inicial: catálogo filtrado por allowedProductOptions e excludedProducts
  const catalogFiltered = allowedProductOptions?.length
    ? catalogBase.filter((option) => allowedProductOptions.includes(option))
    : catalogBase;
  const availableProducts = catalogFiltered.filter((option) => !excludedProducts.includes(option));
  const initialProduct =
    defaultProduct &&
    !excludedProducts.includes(defaultProduct) &&
    availableProducts.includes(defaultProduct)
      ? defaultProduct
      : (availableProducts.find((p) => p !== PRODUTO_LEAD_SEM_PRODUTO) ??
          availableProducts[0] ??
          'Sistema Solar (on-grid)');

  const [produtosSelecionados, setProdutosSelecionados] = useState<string[]>([initialProduct]);
  /** null = ainda não respondeu; true = cliente existente adquirindo novo produto */
  const [ehClienteExistente, setEhClienteExistente] = useState<boolean | null>(null);
  const [clienteExistenteLeadId, setClienteExistenteLeadId] = useState<string | null>(null);
  const [produtosDoCliente, setProdutosDoCliente] = useState<string[]>([]);
  const [documentoDuplicado, setDocumentoDuplicado] = useState<{ nome: string } | null>(null);
  const [verificandoDocumento, setVerificandoDocumento] = useState(false);
  const [carregandoCliente, setCarregandoCliente] = useState(false);

  const defaultValuesVendas: LeadForm = {
    tipo: 'fisica',
    nome: '',
    razaoSocial: '',
    cpf: '',
    cnpj: '',
    representanteLegal: '',
    cpfRepresentante: '',
    email: '',
    telefone: '',
    endereco: '',
    numero: '',
    cidade: '',
    estado: '',
    cep: '',
    tipoFixacao: null,
    tipoTelha: '',
    origem: '',
    origemOutro: '',
    produto: initialProduct as LeadForm['produto'],
    consumoMensal: null,
    mensagem: '',
    vendedor_id: '',
    parceiro_id: '',
    faturas: undefined,
  };

  const defaultValuesParceiro: LeadFormParceiroCrm = {
    tipo: 'fisica',
    nome: '',
    razaoSocial: '',
    cpf: '',
    cnpj: '',
    representanteLegal: '',
    cpfRepresentante: '',
    email: '',
    telefone: '',
    endereco: '',
    numero: '',
    cidade: '',
    estado: '',
    cep: '',
    tipoFixacao: null,
    tipoTelha: '',
    origem: '',
    origemOutro: '',
    produto: PRODUTO_LEAD_PARCERIA_CRM,
    consumoMensal: null,
    mensagem: '',
    vendedor_id: '',
    parceiro_id: '',
    faturas: undefined,
    crmFunil: CRM_FUNIL_PARCERIA_PROSPECCAO,
  };

  const form = useForm<LeadForm | LeadFormParceiroCrm>({
    resolver: zodResolver(isParceiroCrm ? leadSchemaParceiroCrm : leadSchema) as any,
    defaultValues: isParceiroCrm ? defaultValuesParceiro : defaultValuesVendas,
  });

  const { control, handleSubmit, watch, setValue, formState: { errors, isSubmitting } } = form;
  const tipo = watch('tipo');
  const cpf = watch('cpf');
  const cnpj = watch('cnpj');
  const tipoFixacao = watch('tipoFixacao');
  const origem = watch('origem');
  const origemOutro = watch('origemOutro');
  const produto = watch('produto');
  const vendedor_id = watch('vendedor_id');
  const parceiro_id = watch('parceiro_id');
  
  // Verificar se o produto selecionado é um sistema solar que requer os campos de fixação, consumo e faturas
  const isSistemaSolar = !isParceiroCrm && isProdutoLeadSolarComoOnGrid(produto);

  // Determinar vendedor para filtrar parceiros conforme perfil
  const vendedorIdForParceiros = isAdmin
    ? (vendedor_id || undefined)
    : (isRepresentative || isSeller)
      ? (permissions?.userProfile?.id || undefined)
      : undefined;

  const { parceiros, loading: parceirosLoading } = useParceiros(vendedorIdForParceiros);
  const faturasInputRef = useRef<HTMLInputElement>(null);

  // Efeitos para preparar campos conforme o perfil
  useEffect(() => {
    if ((isRepresentative || isSeller) && permissions?.userProfile?.id) {
      if (vendedor_id !== permissions.userProfile.id) {
        setValue('vendedor_id', permissions.userProfile.id);
      }
    }
  }, [isRepresentative, isSeller, permissions?.userProfile?.id, setValue, vendedor_id]);

  // Preencher vendedor_id e parceiro_id automaticamente quando é parceiro
  useEffect(() => {
    if (isPartner && permissions?.userProfile) {
      // Parceiro: preenche seu próprio ID e o ID do vendedor vinculado
      if (parceiro_id !== permissions.userProfile.id) {
        setValue('parceiro_id', permissions.userProfile.id);
      }
      
      // O vendedor_id vem do campo vendedor_id do perfil do parceiro
      if (permissions.userProfile.vendedor_id) {
        if (vendedor_id !== permissions.userProfile.vendedor_id) {
          setValue('vendedor_id', permissions.userProfile.vendedor_id);
          console.log('✅ Parceiro detectado - vendedor vinculado:', permissions.userProfile.vendedor_id);
        }
      } else {
        console.error('❌ ERRO: Parceiro sem vendedor vinculado!');
      }
    }
  }, [isPartner, permissions?.userProfile, setValue, parceiro_id, vendedor_id]);

  // Limpar parceiro somente quando o admin trocar o vendedor — não incluir parceiro_id nas deps,
  // senão qualquer seleção de parceiro reexecuta o efeito e zera o campo de novo.
  const prevVendedorIdForParceiroRef = useRef<string | undefined>(vendedor_id);
  useEffect(() => {
    if (!isAdmin) {
      prevVendedorIdForParceiroRef.current = vendedor_id;
      return;
    }
    const prev = prevVendedorIdForParceiroRef.current;
    if (prev !== vendedor_id && prev !== undefined && prev !== '') {
      setValue('parceiro_id', '');
    }
    prevVendedorIdForParceiroRef.current = vendedor_id;
  }, [isAdmin, vendedor_id, setValue]);

  // Parceiro: origem sempre "Parceiros" e não editável (exceto no funil CRM de parcerias, onde a origem é livre)
  useEffect(() => {
    if (isPartner && !isParceiroCrm) {
      if (origem !== 'Parceiros') {
        setValue('origem', 'Parceiros');
      }
      if (origemOutro !== '') {
        setValue('origemOutro', '');
      }
    }
  }, [isPartner, isParceiroCrm, setValue, origem, origemOutro]);

  // Vendedor: restringir opções de origem e validar seleção (funil parceiro CRM: todas as origens)
  const vendedorOrigemAllow = new Set(["Leads de Representantes", "Parceiros", "Outro"]);
  const origemOptionsFiltered =
    isSeller && !isParceiroCrm ? origemOptions.filter((o) => vendedorOrigemAllow.has(o)) : origemOptions;

  useEffect(() => {
    if (isSeller && !isParceiroCrm && origem && !vendedorOrigemAllow.has(origem)) {
      setValue('origem', '');
      setValue('origemOutro', '');
    }
  }, [isSeller, isParceiroCrm, origem, setValue]);

  // Aplicar defaultProduct só quando a prop ou excludedProducts mudam (ex.: abrir o modal).
  // Não depender de `produto`: senão, na página Carregadores (default = Wallbox), ao escolher
  // "Estudo de Viabilidade - EV" o efeito reexecutava e voltava a forçar Wallbox.
  useEffect(() => {
    if (!defaultProduct || excludedProducts.includes(defaultProduct)) return;
    if (allowedProductOptions?.length && !allowedProductOptions.includes(defaultProduct)) return;
    setValue('produto', defaultProduct);
    if (allowMultipleProducts) {
      setProdutosSelecionados([defaultProduct]);
    }
  }, [allowedProductOptions, allowMultipleProducts, defaultProduct, excludedProducts, setValue]);

  const resetClienteExistente = () => {
    setClienteExistenteLeadId(null);
    setProdutosDoCliente([]);
    setDocumentoDuplicado(null);
  };

  const preencherFormComLead = (lead: Record<string, unknown>) => {
    const tipoLead = lead.tipo === 'juridica' ? 'juridica' : 'fisica';
    setValue('tipo', tipoLead);
    setValue('nome', (lead.nome as string) || '');
    setValue('razaoSocial', (lead.razaoSocial as string) || (lead.razao_social as string) || '');
    setValue('cpf', (lead.cpf as string) || '');
    setValue('cnpj', (lead.cnpj as string) || '');
    setValue('representanteLegal', (lead.representanteLegal as string) || (lead.representante_legal as string) || '');
    setValue('cpfRepresentante', (lead.cpfRepresentante as string) || (lead.cpf_representante as string) || '');
    setValue('email', (lead.email as string) || '');
    setValue('telefone', (lead.telefone as string) || '');
    setValue('cep', (lead.cep as string) || '');
    setValue('endereco', (lead.endereco as string) || '');
    setValue('numero', (lead.numero as string) || '');
    setValue('cidade', (lead.cidade as string) || '');
    setValue('estado', (lead.estado as string) || '');
    if (lead.vendedor_id) setValue('vendedor_id', lead.vendedor_id as string);
    if (lead.parceiro_id) setValue('parceiro_id', lead.parceiro_id as string);
    if (lead.origem) setValue('origem', lead.origem as string);
  };

  const carregarProdutosDoCliente = async (leadId: string) => {
    try {
      const res = await fetchWithAuth(`/api/leads/${leadId}/irmaos`);
      if (!res.ok) return;
      const data = await res.json();
      const produtos = Array.isArray(data.irmaos)
        ? data.irmaos.map((i: { produto?: string | null }) => i.produto).filter(Boolean)
        : [];
      setProdutosDoCliente(produtos as string[]);
    } catch {
      setProdutosDoCliente([]);
    }
  };

  const handleSelecionarClienteExistente = async (cliente: ClienteCadastroOption | null) => {
    if (!cliente) {
      resetClienteExistente();
      return;
    }
    setCarregandoCliente(true);
    setClienteExistenteLeadId(cliente.id);
    setDocumentoDuplicado(null);
    try {
      const res = await fetchWithAuth(`/api/leads/${cliente.id}`);
      if (!res.ok) throw new Error('Erro ao carregar dados do cliente');
      const lead = await res.json();
      preencherFormComLead(lead);
      await carregarProdutosDoCliente(cliente.id);
    } catch (error) {
      console.error(error);
      toast.error('Não foi possível carregar os dados do cliente selecionado');
      resetClienteExistente();
    } finally {
      setCarregandoCliente(false);
    }
  };

  const verificarDocumentoDuplicado = async (documento: string) => {
    if (ehClienteExistente) return;
    const digits = documento.replace(/\D/g, '');
    const minLen = tipo === 'juridica' ? 14 : 11;
    if (digits.length < minLen) {
      setDocumentoDuplicado(null);
      return;
    }

    setVerificandoDocumento(true);
    try {
      const params = new URLSearchParams({ tipo });
      if (tipo === 'juridica') {
        params.set('cnpj', digits);
      } else {
        params.set('cpf', digits);
      }
      const res = await fetchWithAuth(`/api/leads/cadastro/verificar-documento?${params.toString()}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data.existe) {
        setDocumentoDuplicado({ nome: data.nome || 'Cliente cadastrado' });
      } else {
        setDocumentoDuplicado(null);
      }
    } catch {
      // silencioso — validação final no submit
    } finally {
      setVerificandoDocumento(false);
    }
  };

  useEffect(() => {
    if (ehClienteExistente) {
      setDocumentoDuplicado(null);
      return;
    }
    const doc = tipo === 'juridica' ? cnpj : cpf;
    if (!doc) {
      setDocumentoDuplicado(null);
      return;
    }
    const timer = setTimeout(() => {
      void verificarDocumentoDuplicado(doc);
    }, 400);
    return () => clearTimeout(timer);
  }, [cpf, cnpj, tipo, ehClienteExistente]);

  useEffect(() => {
    if (ehClienteExistente === false) {
      resetClienteExistente();
    }
  }, [ehClienteExistente]);

  const produtoSelectDisabled =
    lockProduct && !(allowedProductOptions && allowedProductOptions.length > 1);

  const toggleProdutoMulti = (option: string, checked: boolean) => {
    setProdutosSelecionados((prev) => {
      let next: string[];
      if (checked) {
        next = prev.includes(option) ? prev : [...prev, option];
      } else {
        next = prev.filter((p) => p !== option);
        if (next.length === 0) {
          toast.error('Selecione ao menos um produto');
          return prev;
        }
      }
      setValue('produto', next[0] as LeadForm['produto'], { shouldValidate: true });
      return next;
    });
  };

  // Verificar permissões (parceiro pode criar lead mesmo sem permissão explícita)
  const canCreateLead = (permissions?.leads?.canCreate() || false) || (permissions?.isPartner?.() || false);
  const canAssignVendedor = isAdmin; // apenas admin escolhe vendedor
  const canAssignParceiro = (isAdmin || isRepresentative || isSeller) && !isPartner;

  if (!canCreateLead) {
    return (
      <div className="p-6">
        <div className="text-center">
          <h2 className="text-lg font-semibold text-red-600 mb-2">Acesso Negado</h2>
          <p className="text-muted-foreground">Você não tem permissão para criar leads.</p>
          <Button onClick={onClose} variant="outline" className="mt-4">Fechar</Button>
        </div>
      </div>
    );
  }

  const onSubmit = async (data: LeadForm | LeadFormParceiroCrm) => {
    try {
      if (ehClienteExistente === null) {
        toast.error('Informe se o lead já é cliente da empresa');
        return;
      }
      if (ehClienteExistente && !clienteExistenteLeadId) {
        toast.error('Selecione o cliente existente na lista');
        return;
      }
      if (!ehClienteExistente && documentoDuplicado) {
        const docLabel = data.tipo === 'juridica' ? 'CNPJ' : 'CPF';
        toast.error(`Já existe um cliente com este ${docLabel}: ${documentoDuplicado.nome}`, {
          description: 'Utilize a opção "Cliente existente" para adicionar um novo produto.',
          duration: 8000,
        });
        return;
      }

      // Limpar campos vazios
      if (data.nome) data.nome = formatNomeProprio(data.nome);
      if (data.razaoSocial) data.razaoSocial = formatNomeProprio(data.razaoSocial);
      if (data.representanteLegal) data.representanteLegal = formatNomeProprio(data.representanteLegal);

      if (!data.vendedor_id || data.vendedor_id === '') {
        data.vendedor_id = undefined;
      }
      if (!data.parceiro_id || data.parceiro_id === '') {
        data.parceiro_id = undefined;
      }
      if (!data.consumoMensal) {
        data.consumoMensal = null;
      }

      // Log para debug
      console.log('📤 Enviando lead:', {
        vendedor_id: data.vendedor_id,
        parceiro_id: data.parceiro_id,
        origem: data.origem,
        tipo: data.tipo
      });

      // Enviar para endpoint de upload (aceita multipart com arquivos)
      const formData = new FormData();
      
      // Sem faturas no JSON; sem opt-in de tarefas no cadastro (ajuste em Ver detalhes → Tarefas)
      const { faturas, seguirProcessoTarefas: _omitSeguirFunil, ...leadDataWithoutFiles } = data;
      const produtosPayload =
        allowMultipleProducts && produtosSelecionados.length > 0
          ? produtosSelecionados
          : [data.produto];
      const formValues = form.getValues();
      const leadPayload = {
        ...leadDataWithoutFiles,
        cnpj: formValues.cnpj || leadDataWithoutFiles.cnpj,
        cpf: formValues.cpf || leadDataWithoutFiles.cpf,
        produto: produtosPayload[0],
        produtos: produtosPayload,
        ehClienteExistente: !!ehClienteExistente,
        clienteExistenteLeadId: ehClienteExistente ? clienteExistenteLeadId : undefined,
      };
      formData.append('lead_data', JSON.stringify(leadPayload));
      
      // Anexar arquivos de faturas se houver
      if (faturas && faturas instanceof FileList) {
        for (let i = 0; i < faturas.length; i++) {
          formData.append('fatura', faturas[i]);
        }
      } else if (faturas && Array.isArray(faturas)) {
        faturas.forEach((file: File) => {
          formData.append('fatura', file);
        });
      }

      const response = await fetchWithAuth('/api/leads/upload', {
        method: 'POST',
        // Não definir Content-Type manualmente; o browser define boundary correto
        body: formData as any,
      });

      if (!response.ok) {
        let message = 'Erro ao criar lead';
        try {
          const errorData = await response.json();
          message = errorData.error || errorData.message || message;
        } catch {
          try {
            const text = await response.text();
            message = `${message}: ${text.substring(0, 300)}`;
          } catch {}
        }
        // 409 = documento duplicado para o mesmo produto
        if (response.status === 409) {
          const isPj = data.tipo === 'juridica';
          const docExibido = isPj
            ? (data.cnpj || 'informado')
            : ((data.tipo === 'fisica' ? data.cpf : data.cpfRepresentante) || 'informado');
          toast.error(
            isPj
              ? `Lead com CNPJ ${docExibido} já cadastrado`
              : `Lead com CPF ${docExibido} já cadastrado`,
            {
              description: message,
              duration: 8000,
            }
          );
          if (message.includes('Utilize a opção')) {
            toast.info('Selecione "Cliente existente" para vincular um novo produto a este cadastro.', {
              duration: 6000,
            });
          }
        } else {
          toast.error(message);
        }
        throw new Error(message);
      }

      const result = await response.json();
      console.log('Lead criado com sucesso:', result);
      const qtd = Array.isArray(result.lead_ids) ? result.lead_ids.length : 1;
      toast.success(
        ehClienteExistente
          ? qtd > 1
            ? `${qtd} novas negociações criadas para o cliente`
            : 'Nova negociação criada para o cliente existente'
          : qtd > 1
            ? `${qtd} negociações criadas (um card por produto no CRM)`
            : 'Lead criado com sucesso'
      );
      
      // Limpar formulário
      form.reset();
      
      // Chamar callback de sucesso
      if (onSuccess) {
        onSuccess();
      }
      
      // Fechar modal
      onClose();
      
    } catch (error) {
      console.error('Erro ao criar lead:', error);
      // Toast já exibido no bloco !response.ok; para outros erros, exibir aqui
      if (error instanceof Error && !error.message.includes('Já existe um lead cadastrado')) {
        toast.error(error.message || 'Erro ao criar lead');
      }
    }
  };

  const onInvalid = (errors: any) => {
    console.log('❌ Erros de validação encontrados:');
    console.table(errors);
    Object.keys(errors).forEach((key) => {
      console.log(`Campo "${key}":`, errors[key]?.message || errors[key]);
    });
  };

  const fetchAddressFromCEP = async (cepValue: string) => {
    if (cepValue.length === 8) {
      try {
        const response = await fetch(`https://viacep.com.br/ws/${cepValue}/json/`);
        const data = await response.json();
        if (!data.erro) {
          setValue('endereco', data.logradouro);
          setValue('cidade', data.localidade);
          setValue('estado', data.uf);
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
      }
    }
  };

  return (
    <div className="p-6">

      <Form {...form}>
        <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
          <FormItem>
            <FormLabel>Situação do lead *</FormLabel>
            <Select
              value={ehClienteExistente === null ? '' : ehClienteExistente ? 'existente' : 'novo'}
              onValueChange={(v) => {
                setEhClienteExistente(v === 'existente');
                if (v !== 'existente') {
                  resetClienteExistente();
                }
              }}
            >
              <FormControl>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a situação" />
                </SelectTrigger>
              </FormControl>
              <SelectContent>
                <SelectItem value="novo">Novo cliente</SelectItem>
                <SelectItem value="existente">Cliente existente (novo produto)</SelectItem>
              </SelectContent>
            </Select>
            {ehClienteExistente === true && (
              <p className="text-xs text-muted-foreground mt-1">
                Os dados cadastrais são compartilhados entre todos os produtos do cliente. Alterações aqui
                atualizam todas as negociações vinculadas.
              </p>
            )}
          </FormItem>

          <FormField
            control={control}
            name="tipo"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Tipo *</FormLabel>
                <Select
                  onValueChange={(v) => {
                    field.onChange(v);
                    if (ehClienteExistente) {
                      resetClienteExistente();
                    }
                  }}
                  value={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o tipo" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="fisica">Pessoa Física</SelectItem>
                    <SelectItem value="juridica">Pessoa Jurídica</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {ehClienteExistente === true && (
            <FormItem>
              <FormLabel>Cliente *</FormLabel>
              <ClienteExistenteCombobox
                tipo={tipo === 'juridica' ? 'juridica' : 'fisica'}
                value={clienteExistenteLeadId}
                onSelect={handleSelecionarClienteExistente}
                crmFunil={crmFunil}
                disabled={carregandoCliente}
              />
              {carregandoCliente && (
                <p className="text-xs text-muted-foreground">Carregando dados do cliente...</p>
              )}
              {produtosDoCliente.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Produtos já vinculados: {produtosDoCliente.join(', ')}
                </p>
              )}
            </FormItem>
          )}

          {ehClienteExistente === false && documentoDuplicado && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Cliente já cadastrado</AlertTitle>
              <AlertDescription>
                O {tipo === 'juridica' ? 'CNPJ' : 'CPF'} informado pertence a{' '}
                <strong>{documentoDuplicado.nome}</strong>. Selecione &quot;Cliente existente&quot; para
                adicionar um novo produto ou utilize outro documento.
              </AlertDescription>
            </Alert>
          )}

          {tipo === 'fisica' ? (
            <FormField
              control={control}
              name="nome"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ''}
                      onBlur={(e) => {
                        field.onBlur();
                        aplicarFormatoNomeNoBlur(e.target.value, field.onChange);
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ) : (
            <>
              <FormField
                control={control}
                name="razaoSocial"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Razão Social *</FormLabel>
                    <FormControl>
                      <Input
                      {...field}
                      value={field.value || ''}
                      onBlur={(e) => {
                        field.onBlur();
                        aplicarFormatoNomeNoBlur(e.target.value, field.onChange);
                      }}
                    />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="cnpj"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CNPJ</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={formatCNPJ(field.value || '')}
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                        placeholder="00.000.000/0000-00"
                        readOnly={!!ehClienteExistente && !!clienteExistenteLeadId}
                        className={
                          ehClienteExistente && clienteExistenteLeadId
                            ? 'bg-muted cursor-not-allowed'
                            : undefined
                        }
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="representanteLegal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Representante Legal *</FormLabel>
                    <FormControl>
                      <Input
                      {...field}
                      value={field.value || ''}
                      onBlur={(e) => {
                        field.onBlur();
                        aplicarFormatoNomeNoBlur(e.target.value, field.onChange);
                      }}
                    />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="cpfRepresentante"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>CPF do Representante</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        value={formatCPF(field.value || '')}
                        onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                        placeholder="000.000.000-00"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </>
          )}

          {tipo === 'fisica' && (
            <FormField
              control={control}
              name="cpf"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>CPF</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={formatCPF(field.value || '')}
                      onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                      placeholder="000.000.000-00"
                      readOnly={!!ehClienteExistente && !!clienteExistenteLeadId}
                      className={
                        ehClienteExistente && clienteExistenteLeadId
                          ? 'bg-muted cursor-not-allowed'
                          : undefined
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          <FormField
            control={control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} type="email" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="telefone"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Telefone *</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formatTelefone(field.value || '')}
                    onChange={(e) => field.onChange(e.target.value.replace(/\D/g, ''))}
                    placeholder="(00) 00000-0000"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="cep"
            render={({ field }) => (
              <FormItem>
                <FormLabel>CEP</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={formatCEP(field.value || '')}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      field.onChange(value);
                      fetchAddressFromCEP(value);
                    }}
                    placeholder="00000-000"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="endereco"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Endereço</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="numero"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Número</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="cidade"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Cidade</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={control}
            name="estado"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Estado</FormLabel>
                <FormControl>
                  <Input {...field} value={field.value || ''} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          {!isParceiroCrm && (
            <FormField
              control={control}
              name="produto"
              render={({ field }) => {
                const availableProductsField = catalogFiltered.filter(
                  (option) => !excludedProducts.includes(option)
                );

                if (allowMultipleProducts) {
                  return (
                    <FormItem>
                      <FormLabel>Produtos interessados *</FormLabel>
                      <p className="text-xs text-muted-foreground mb-2">
                        Cada produto gera uma negociação separada no CRM (um card por produto).
                      </p>
                      <div className="space-y-2 rounded-md border p-3">
                        {availableProductsField.map((option) => {
                          const checked = produtosSelecionados.includes(option);
                          const jaPossui = produtosDoCliente.includes(option);
                          const bloqueado = jaPossui && !allowDuplicateProducts;
                          return (
                            <label
                              key={option}
                              className={`flex items-start gap-2 text-sm ${
                                bloqueado ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                              }`}
                            >
                              <Checkbox
                                checked={checked}
                                disabled={bloqueado}
                                onCheckedChange={(v) => {
                                  if (!bloqueado) toggleProdutoMulti(option, v === true);
                                }}
                                className="mt-0.5"
                              />
                              <span>
                                {option}
                                {jaPossui && (
                                  <span className="text-xs text-muted-foreground ml-1">
                                    {allowDuplicateProducts
                                      ? '(já possui — nova negociação)'
                                      : '(já cadastrado)'}
                                  </span>
                                )}
                              </span>
                            </label>
                          );
                        })}
                      </div>
                      {produtosSelecionados.length > 1 && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {tipo === 'juridica'
                            ? 'Informe o CNPJ para vincular os produtos ao mesmo cliente.'
                            : 'Informe o CPF para vincular os produtos ao mesmo cliente.'}
                        </p>
                      )}
                      <FormMessage />
                    </FormItem>
                  );
                }

                return (
                  <FormItem>
                    <FormLabel>Produto *</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      value={field.value || ''}
                      disabled={produtoSelectDisabled}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o produto" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {availableProductsField.map((option) => {
                          const jaPossui = produtosDoCliente.includes(option);
                          const bloqueado = jaPossui && !allowDuplicateProducts;
                          return (
                            <SelectItem key={option} value={option} disabled={bloqueado}>
                              {option}
                              {jaPossui
                                ? allowDuplicateProducts
                                  ? ' (já possui — nova negociação)'
                                  : ' (já cadastrado)'
                                : ''}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          )}

          <FormField
            control={control}
            name="origem"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Origem *</FormLabel>
                <Select onValueChange={field.onChange} value={field.value || ''} disabled={isPartner && !isParceiroCrm}>
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione a origem" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {origemOptionsFiltered.map((option) => (
                      <SelectItem key={option} value={option}>
                        {option}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {origem === 'Outro' && (
            <FormField
              control={control}
              name="origemOutro"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Especifique a origem *</FormLabel>
                  <FormControl>
                    <Input {...field} value={field.value || ''} placeholder="Digite a origem" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {canAssignVendedor && (
            <FormField
              control={control}
              name="vendedor_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Vendedor *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''} disabled={vendedoresLoading}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={vendedoresLoading ? "Carregando..." : "Selecione um vendedor"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {!vendedoresLoading && vendedores.length > 0 ? (
                        vendedores.map((vendedor) => (
                          <SelectItem key={vendedor.id} value={vendedor.id}>
                            {vendedor.nome}
                          </SelectItem>
                        ))
                      ) : !vendedoresLoading && vendedores.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                          Nenhum vendedor disponível
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {canAssignParceiro && (
            <FormField
              control={control}
              name="parceiro_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Parceiro</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value || ''} disabled={parceirosLoading}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder={parceirosLoading ? "Carregando..." : "Selecione um parceiro"} />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {!parceirosLoading && parceiros.length > 0 ? (
                        parceiros.map((parceiro) => (
                          <SelectItem key={parceiro.id} value={parceiro.id}>
                            {parceiro.nome}
                          </SelectItem>
                        ))
                      ) : !parceirosLoading && parceiros.length === 0 ? (
                        <div className="px-2 py-3 text-sm text-muted-foreground text-center">
                          Nenhum parceiro disponível
                        </div>
                      ) : null}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {isSistemaSolar && (
            <>
              <FormField
                control={control}
                name="tipoFixacao"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo de Fixação</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value || ''}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o tipo de fixação" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {tipoFixacaoOptions.map((option) => (
                          <SelectItem key={option} value={option}>
                            {option}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {tipoFixacao === 'Telhado' && (
                <FormField
                  control={control}
                  name="tipoTelha"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Tipo de Telha *</FormLabel>
                      <Select onValueChange={field.onChange} value={field.value || ''}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecione o tipo de telha" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {tipoTelhaOptions.map((option) => (
                            <SelectItem key={option} value={option}>
                              {option}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={control}
                name="consumoMensal"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Consumo Mensal (kWh)</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type="number"
                        value={field.value || ''}
                        onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                        placeholder="Ex: 500"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={control}
                name="faturas"
                render={({ field: { value, onChange, ...fieldProps } }) => {
                  const filesArray: File[] = !value
                    ? []
                    : value instanceof FileList
                      ? Array.from(value)
                      : Array.isArray(value)
                        ? value
                        : [];
                  return (
                    <FormItem>
                      <FormLabel>Faturas de Energia (Opcional)</FormLabel>
                      <FormControl>
                        <div className="space-y-2">
                          <div className="flex items-center gap-3 flex-wrap">
                            <label className="cursor-pointer">
                              <div className="px-6 py-2 bg-sky-50 hover:bg-sky-100 rounded-md border border-sky-200 transition-colors whitespace-nowrap">
                                <span className="text-blue-600 font-medium">Escolher Arquivos</span>
                              </div>
                              <input
                                {...fieldProps}
                                ref={(el) => {
                                  faturasInputRef.current = el;
                                  if (typeof fieldProps.ref === 'function') fieldProps.ref(el);
                                  else if (fieldProps.ref) (fieldProps.ref as React.MutableRefObject<HTMLInputElement | null>).current = el;
                                }}
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                multiple
                                onChange={(e) => {
                                  const list = e.target.files;
                                  if (!list || list.length === 0) return;
                                  const next = Array.from(list);
                                  onChange(next);
                                }}
                                className="hidden"
                              />
                            </label>
                            <span className="text-gray-500 text-sm">
                              {filesArray.length > 0
                                ? `${filesArray.length} arquivo(s) selecionado(s)`
                                : 'Nenhum arquivo selecionado'
                              }
                            </span>
                          </div>
                          {filesArray.length > 0 && (
                            <ul className="space-y-1.5">
                              {filesArray.map((file, index) => (
                                <li
                                  key={`${file.name}-${index}`}
                                  className="flex items-center justify-between gap-2 py-1.5 px-2 rounded bg-sky-50/80 border border-sky-100 text-sm"
                                >
                                  <span className="truncate text-gray-700" title={file.name}>
                                    {file.name}
                                  </span>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="h-7 w-7 shrink-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                                    aria-label="Remover arquivo"
                                    onClick={() => {
                                      const next = filesArray.filter((_, i) => i !== index);
                                      onChange(next.length > 0 ? next : undefined);
                                      if (next.length === 0 && faturasInputRef.current) {
                                        faturasInputRef.current.value = '';
                                      }
                                    }}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </li>
                              ))}
                            </ul>
                          )}
                          <p className="text-sm text-gray-500">
                            Formatos aceitos: PDF, JPG, PNG (máx. 5MB por arquivo)
                          </p>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  );
                }}
              />
            </>
          )}

          <FormField
            control={control}
            name="mensagem"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{isParceiroCrm ? 'Registro do contato / próximos passos' : 'Observações'}</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value || ''}
                    placeholder={
                      isParceiroCrm
                        ? 'Ex.: data do contato, retorno combinado, decisores envolvidos…'
                        : 'Observações adicionais...'
                    }
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <p className="text-xs text-muted-foreground">* Campos obrigatórios</p>
          <Button
            type="submit"
            disabled={
              isSubmitting ||
              ehClienteExistente === null ||
              verificandoDocumento ||
              (!ehClienteExistente && !!documentoDuplicado) ||
              (ehClienteExistente === true && !clienteExistenteLeadId)
            }
          >
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </form>
      </Form>
    </div>
  );
}